/**
 * Static CDN viewer entry — mounts ShareViewer from bootstrap + freeze.json.
 */
import { createRoot } from 'react-dom/client';
import type { PublicShareManifest } from '@containd/shared';
import { parseCanvasParentFromPathname } from '@containd/shared';
import { ShareViewer } from '../components/ShareViewer';
import {
  buildShareMediaBaseUrl,
  setShareMediaBaseUrl,
} from '../lib/shareMediaUrl';
import '../app/globals.css';

const VIEWER_CSS_HREF = '/_assets/viewer.css';

/** Ensure viewer CSS loads even if materialize omitted the link (legacy pages). */
function ensureViewerStylesheet(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('link[data-containd-viewer-css]')) return;
  const existing = document.querySelector(
    `link[rel="stylesheet"][href="${VIEWER_CSS_HREF}"]`,
  );
  if (existing) {
    existing.setAttribute('data-containd-viewer-css', '1');
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = VIEWER_CSS_HREF;
  link.setAttribute('data-containd-viewer-css', '1');
  document.head.appendChild(link);
}

ensureViewerStylesheet();

const AUTH_SESSION_SRC = '/_assets/auth-session.js';

function ensureAuthSession(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-containd-auth-session]')) return;
  const existing = document.querySelector(
    `script[src="${AUTH_SESSION_SRC}"], script[src^="${AUTH_SESSION_SRC}?"]`,
  );
  if (existing) {
    existing.setAttribute('data-containd-auth-session', '1');
    return;
  }
  const script = document.createElement('script');
  script.src = AUTH_SESSION_SRC;
  script.setAttribute('data-containd-auth-session', '1');
  document.head.appendChild(script);
}

ensureAuthSession();

export type ContaindShareBootstrap = {
  schemaVersion: 1;
  visibility: 'public' | 'private';
  shareId: string;
  version: number;
  freezeVersion: number;
  slug: string | null;
  token: string | null;
  freezeUrl: string;
  mediaBaseUrl: string;
  reportApiUrl: string;
  canonicalUrl: string;
  softLaunchNoindex: boolean;
  theme: 'light' | 'dark';
  title: string;
  initialParentUuid: string | null;
  initialItemUuid: string | null;
  viewerEntry: string;
  embedded?: boolean;
  embeddable?: boolean;
};

declare global {
  interface Window {
    __CONTAIND_SHARE__?: ContaindShareBootstrap;
  }
}

async function loadFreeze(
  boot: ContaindShareBootstrap,
): Promise<PublicShareManifest> {
  const url = resolveFreezeFetchUrl(boot);
  let res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`freeze ${res.status}`);
  let manifest = (await res.json()) as PublicShareManifest;
  if (manifest.version !== boot.version) {
    const refetchBase = url.includes('?') ? url.split('?')[0]! : url;
    res = await fetch(`${refetchBase}?v=${boot.version}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`freeze refetch ${res.status}`);
    manifest = (await res.json()) as PublicShareManifest;
  }
  return manifest;
}

/**
 * Prefer absolute freeze paths. Legacy `./freeze.json` breaks when the page URL
 * has no trailing slash (`/s/slug` → `/s/freeze.json` 404).
 */
function resolveFreezeFetchUrl(boot: ContaindShareBootstrap): string {
  const raw = (boot.freezeUrl || './freeze.json').trim();
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return raw;
  }
  if (boot.visibility === 'public' && boot.slug) {
    return `/s/${encodeURIComponent(boot.slug)}/freeze.json`;
  }
  if (boot.token) {
    return `/share/${encodeURIComponent(boot.token)}/freeze.json`;
  }
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    const dir = path.endsWith('/') ? path : path.replace(/\/[^/]*$/, '/');
    return `${dir}${raw.replace(/^\.\//, '')}`;
  }
  return raw;
}

function markViewerReady(rootEl: HTMLElement): void {
  document.getElementById('share-loading')?.remove();
  const seo = document.getElementById('share-seo');
  if (seo) seo.hidden = true;
  rootEl.setAttribute('data-viewer-ready', '1');
}

async function main() {
  const boot = window.__CONTAIND_SHARE__;
  const rootEl = document.getElementById('share-root');
  if (!boot || !rootEl) {
    console.error('[viewer] missing bootstrap or #share-root');
    return;
  }

  setShareMediaBaseUrl(
    boot.mediaBaseUrl ||
      buildShareMediaBaseUrl(boot.shareId, boot.version),
  );

  try {
    const manifest = await loadFreeze(boot);
    const fromPath = parseCanvasParentFromPathname(
      typeof window !== 'undefined' ? window.location.pathname : '',
      manifest,
    );
    const itemFromQuery =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('item')
        : null;
    // Clear static loading before React mounts so we don't nest under #share-loading.
    markViewerReady(rootEl);
    createRoot(rootEl).render(
      <ShareViewer
        manifest={manifest}
        initialParentUuid={fromPath ?? boot.initialParentUuid}
        initialItemUuid={itemFromQuery || boot.initialItemUuid}
        embedded={boot.embedded === true}
        embeddable={boot.embeddable === true}
      />,
    );
  } catch (err) {
    markViewerReady(rootEl);
    rootEl.innerHTML = `<p style="padding:24px;font-family:system-ui">Failed to load share: ${
      err instanceof Error ? err.message : String(err)
    }</p>`;
  }
}

void main();
